import type { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import type * as React from "react";
import { forwardRef } from "react";

import { transientOptions } from "../utilities";
import { defaultCurveConfig } from "./curve";
import type {
  ConnectorCurveDescriptor,
  ConnectorEnd,
  ConnectorInteraction,
  ConnectorState,
  CurvableConnector,
  CurveConfig,
  Edge,
} from "./types";

const edgeToArrowRotation: Record<Edge, number> = {
  TOP: 0,
  RIGHT: 90,
  BOTTOM: 180,
  LEFT: 270,
};

const getArrowRotation = (edge: Edge) => edgeToArrowRotation[edge];

const getPathStateStyles = ({ theme, state, color }: { theme: Theme; state?: ConnectorState; color?: string }) => {
  switch (state) {
    case "active":
      return `
        stroke: ${color ? color : theme.primarySelection};
        stroke-width: 2px;
      `;

    case "highlighted":
      return `
        stroke: ${theme.moduleMap.connectorColor};
        stroke-width: 2px;
      `;

    case "inactive":
      return `
        opacity: 0.6;
        stroke: ${theme.moduleMap.connectorColor};
        stroke-width: 1px;
      `;

    case "hovered":
      return `
        stroke: ${theme.primary};
        stroke-width: 2px;
      `;

    case "moving":
      return `
        stroke: ${color ? color : theme.moduleMap.connectorColor};
        stroke-width: 3px;
        stroke-dasharray: 5 5;
      `;

    case "neutral":
      return `
        stroke: ${theme.moduleMap.connectorColor};
        stroke-width: 2px;
      `;

    default:
      return `
        stroke: ${theme.moduleMap.connectorColor};
        stroke-width: 2px;
      `;
  }
};

const Path = styled.path<{
  theme?: Theme;
  color?: string;
  state?: ConnectorState;
}>`
  ${getPathStateStyles}

  fill: transparent;

  transition:
    stroke 300ms ease-in-out,
    stroke-width 300ms ease-in-out;
`;

const getOuterPathStateStyles = ({ state, color, theme }: { state?: ConnectorState; color?: string; theme: Theme }) => {
  switch (state) {
    case "active":
      return `
        stroke: ${color ? color : theme.moduleMap.background};
        stroke-width: 3px;
      `;

    case "inactive":
      return `
        stroke-width: 3px;
      `;

    case "hovered":
      return `
        stroke-width: 6px;
      `;

    case "moving":
      return `
        stroke-width: 6px;
      `;

    case "neutral":
      return `
        stroke-width: 3px;
      `;

    default:
      return `
        stroke-width: 3px;
      `;
  }
};

const OuterPath = styled.path<{ color?: string; state?: ConnectorState }>`
  stroke: ${({ theme }) => theme.moduleMap.background};

  ${getOuterPathStateStyles}

  fill: transparent;
  &:hover {
    stroke-width: 6px;
  }

  transition:
    stroke 300ms ease-in-out,
    stroke-width 300ms ease-in-out;
`;

const getDotStateStyles = ({ theme, color, state }: { theme: Theme; color?: string; state?: ConnectorState }) => {
  switch (state) {
    case "active":
      return `
        fill: ${color ?? theme.primary};
      `;

    case "inactive":
      return `
        opacity: 0.6;
        fill: ${color ?? theme.moduleMap.connectorColor};
      `;

    case "hovered":
      return `
        fill: ${color ?? theme.primary};
      `;

    case "moving":
      return `
        fill: ${color ?? theme.primary};
      `;

    case "neutral":
      return `
        fill: ${color ?? theme.moduleMap.connectorColor};
      `;

    default:
      return `
        fill: ${color ?? theme.moduleMap.connectorColor};
      `;
  }
};

export const Dot = styled.circle<{ state?: ConnectorState; color?: string }>`
  ${getDotStateStyles}

  transition: all 100ms ease-out;
`;

const getArrowStateStyles = ({ theme, color, state }: { theme: Theme; color?: string; state?: ConnectorState }) => {
  switch (state) {
    case "active":
      return `
        fill: ${color ?? theme.primarySelection};
      `;

    case "highlighted":
      return `
        fill: ${color ?? theme.moduleMap.connectorColor};
      `;

    case "inactive":
      return `
        opacity: 0.6;
        fill: ${theme.moduleMap.connectorColor};
      `;

    case "hovered":
      return `
        fill: ${theme.primary};
      `;

    case "moving":
      return `
        fill: ${color ? color : theme.primary};
      `;

    case "neutral":
      return `
        fill: ${theme.moduleMap.connectorColor};
      `;

    default:
      return `
        fill: ${theme.moduleMap.connectorColor};
      `;
  }
};

type ArrowProps = { size: number } & React.SVGProps<SVGPathElement>;

const UnstyledArrow = forwardRef<SVGPathElement, ArrowProps>(({ size = 0, ...props }, ref) => {
  const halfSize = size / 2;

  return (
    <path
      d={`
    M 0,0
    l ${halfSize},0
    l -${halfSize} ${size}
    l -${halfSize} -${size}
    Z
  `}
      {...props}
      ref={ref}
    />
  );
});
UnstyledArrow.displayName = "Arrow";

export const Arrow = styled(UnstyledArrow, transientOptions)<{ color?: string; state?: ConnectorState }>`
  ${getArrowStateStyles}

  transition: all 100ms ease-out;

  pointer-events: ${({ state }) => (state === "moving" ? "none" : "all")};
`;

type ArrowTouchAreaProps = { size: number } & React.SVGProps<SVGCircleElement>;

const UnstyledArrowTouchArea = forwardRef<SVGCircleElement, ArrowTouchAreaProps>(({ size = 0, ...props }, ref) => (
  <circle r={size ? size / 2 : 0} {...props} ref={ref} />
));
UnstyledArrowTouchArea.displayName = "ArrowTouchArea";

export const ArrowTouchArea = styled(UnstyledArrowTouchArea, transientOptions)<{
  $highlight: boolean;
  $state?: ConnectorState;
}>`
  opacity: ${({ $highlight }) => ($highlight ? 0.125 : 0)};
  fill: ${({ $highlight, theme }) => ($highlight ? theme.secondary : "transparent")};
  pointer-events: ${({ $state }) => ($state === "moving" ? "none" : "all")};
  transition: opacity 0.3s ease-in-out;
`;

const getConnectorSvgDebug = ({
  startOffset,
  startCurve,
  controlPointA,
  controlPointB,
  endCurve,
  endOffset,
}: ConnectorCurveDescriptor) => (
  <g>
    <circle cx={startOffset.x} cy={startOffset.y} r={2} fill="limegreen" />
    <circle cx={startCurve.x} cy={startCurve.y} r={2} fill="limegreen" />
    <line
      x1={startCurve.x}
      y1={startCurve.y}
      x2={controlPointA.x}
      y2={controlPointA.y}
      stroke="limegreen"
      strokeDasharray="5 5"
    />
    <rect
      x={controlPointA.x - 5}
      y={controlPointA.y - 5}
      width={10}
      height={10}
      fill="transparent"
      stroke="limegreen"
    />

    <rect x={controlPointB.x - 5} y={controlPointB.y - 5} width={10} height={10} fill="transparent" stroke="pink" />
    <line
      x1={endCurve.x}
      y1={endCurve.y}
      x2={controlPointB.x}
      y2={controlPointB.y}
      stroke="pink"
      strokeDasharray="5 5"
    />
    <circle cx={endCurve.x} cy={endCurve.y} r={2} fill="pink" />
    <circle cx={endOffset.x} cy={endOffset.y} r={2} fill="pink" />
  </g>
);

const getConnectorSvgPathD = ({
  startOffset,
  startCurve,
  controlPointA,
  controlPointB,
  endCurve,
  endOffset,
}: ConnectorCurveDescriptor) => {
  return `
      M ${startOffset.x},${startOffset.y}
      L ${startCurve.x},${startCurve.y}
      C
        ${controlPointA.x},${controlPointA.y}
        ${controlPointB.x},${controlPointB.y}
        ${endCurve.x},${endCurve.y}
      L ${endOffset.x},${endOffset.y}
    `;
};

export type ConnectorProps = {
  color?: string;
  connector: CurvableConnector;
  debug?: boolean;
  onInteraction?: (interaction: ConnectorInteraction, id: string, end: ConnectorEnd) => void;
  options?: CurveConfig;
  dataAttribute?: string;
};

export const Connector = ({
  color,
  connector,
  debug,
  onInteraction,
  options = defaultCurveConfig,
  dataAttribute,
}: ConnectorProps) => {
  const handlePointerDown = (event: React.PointerEvent, end: ConnectorEnd) => {
    event.stopPropagation();
    onInteraction?.("select", connector.id, end);
  };

  const handleTouchStart = (event: React.TouchEvent, end: ConnectorEnd) => {
    event.stopPropagation();
    onInteraction?.("select", connector.id, end);
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <g data-test="item">
      <g data-test="content-container">
        {/* draw béizer */}
        {
          <g data-test="beizer-curve-container">
            <OuterPath
              data-test="outer-beizer-curve"
              d={getConnectorSvgPathD(connector.curveDescriptor)}
              state={connector.state}
              color={color}
            />
            <Path
              data-test="inner-beizer-curve"
              data-connector-path={dataAttribute}
              d={getConnectorSvgPathD(connector.curveDescriptor)}
              state={connector.state}
              color={color}
            />
          </g>
        }

        {/* draw dot */}
        {!connector.bidirectional && (
          <g
            transform={`translate(${connector.from.anchor.point.x}, ${connector.from.anchor.point.y})`}
            data-test="dot-container"
          >
            <Dot data-connector-dot={dataAttribute} r={options.dotSize} state={connector.state} data-test="dot" />
          </g>
        )}

        {/* draw "from" arrow */}
        {connector.bidirectional && (
          <g
            transform={`
              translate(${connector.from.anchor.point.x}, ${connector.from.anchor.point.y})
              rotate(${getArrowRotation(connector.from.anchor.edge)} 0 0)
              translate(0, -${options.arrowSize + options.arrowGap})
            `}
            data-test="from-arrow-container"
          >
            <ArrowTouchArea
              onTouchStart={(event) => handleTouchStart(event, "from")}
              onTouchEnd={handleTouchEnd}
              size={options.touchableSize}
              $highlight={options.showTouchables}
              $state={connector.state}
            />
            <Arrow
              data-test="from-arrow"
              data-connector-from-arrow={dataAttribute}
              size={options.arrowSize}
              state={connector.state}
              color={color}
              onTouchStart={(event) => handleTouchStart(event, "from")}
              onTouchEnd={handleTouchEnd}
              onPointerDownCapture={(event) => handlePointerDown(event, "from")}
              onMouseEnter={() => onInteraction?.("hover", connector.id, "from")}
              onMouseLeave={() => onInteraction?.("none", connector.id, "from")}
            />
          </g>
        )}

        {/* draw "to" arrow */}
        <g
          transform={`
              translate(${connector.to.anchor.point.x}, ${connector.to.anchor.point.y})
              rotate(${getArrowRotation(connector.to.anchor.edge)} 0 0)
              translate(0, -${options.arrowSize + options.arrowGap})
            `}
          data-test="to-arrow-container"
        >
          <ArrowTouchArea
            onTouchStart={(event) => handleTouchStart(event, "to")}
            onTouchEnd={handleTouchEnd}
            size={options.touchableSize}
            $highlight={options.showTouchables}
            $state={connector.state}
          />
          <Arrow
            data-test="to-arrow"
            data-connector-to-arrow={dataAttribute}
            size={options.arrowSize}
            state={connector.state}
            color={color}
            onTouchStart={(event) => handleTouchStart(event, "to")}
            onTouchEnd={handleTouchEnd}
            onPointerDownCapture={(event) => handlePointerDown(event, "to")}
            onMouseEnter={() => onInteraction?.("hover", connector.id, "to")}
            onMouseLeave={() => onInteraction?.("none", connector.id, "to")}
          />
        </g>
      </g>

      {/* draw debug data */}
      {debug && (
        <g data-test="debug-container">
          <g>{getConnectorSvgDebug(connector.curveDescriptor)}</g>
          <g>
            <rect
              x={connector.from.bounds.x}
              y={connector.from.bounds.y}
              width={connector.from.bounds.width}
              height={connector.from.bounds.height}
              stroke="rgba(0, 0, 255, 0.5)"
              fill="transparent"
            />
            <rect
              x={connector.to.bounds.x - 2}
              y={connector.to.bounds.y - 2}
              width={connector.to.bounds.width + 4}
              height={connector.to.bounds.height + 4}
              stroke="rgb(0, 255, 0, 0.5)"
              strokeDasharray="5 5"
              fill="transparent"
            />
            <line
              x1={connector.from.anchor.point.x}
              y1={connector.from.anchor.point.y}
              x2={connector.to.anchor.point.x}
              y2={connector.to.anchor.point.y}
              stroke="rgba(255, 0, 0, 0.5)"
            />
          </g>
        </g>
      )}
    </g>
  );
};
